/**
 * Several work-order PDFs as one file (#4031).
 *
 * Each work order is fetched from its own `GET /work-orders/:id/pdf`, so the
 * printout is byte-for-byte what the detail page prints, and the files are
 * joined in the browser. Every work order keeps its own pages and its own
 * "page x / y" numbering. pdf-lib is imported on demand: it is needed only when
 * someone presses the button, not on every load of the list.
 */
import { api } from '@/lib/api';

/** Upper bound for one download; the list offers "select all" up to this. */
export const BULK_WORK_ORDER_PDF_MAX = 100;

/** Parallel PDF requests: enough to be quick, few enough not to flood staff-api. */
const FETCH_CONCURRENCY = 4;

export interface BulkPdfTarget {
  id: string;
  /** What the user knows the work order by, for the "left out" message. */
  label: string;
}

export interface BulkPdfResult {
  /** The merged PDF, or null when not one work order could be fetched. */
  pdf: Blob | null;
  /** Work orders whose PDF could not be fetched or read, in list order. */
  failed: BulkPdfTarget[];
}

/**
 * Run `task` for every item with at most `limit` in flight, keeping the results
 * in the input order.
 */
async function mapWithConcurrency<T, R>(
  items: readonly T[],
  limit: number,
  task: (item: T) => Promise<R>,
): Promise<R[]> {
  const results = new Array<R>(items.length);
  let next = 0;
  const worker = async (): Promise<void> => {
    while (next < items.length) {
      const index = next;
      next += 1;
      results[index] = await task(items[index] as T);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return results;
}

/**
 * Fetch the work orders' PDFs and join them in the order given. A work order
 * whose PDF fails is left out and reported, so one broken row does not cost
 * the user the other ninety-nine.
 */
export async function buildBulkWorkOrderPdf(
  targets: readonly BulkPdfTarget[],
  onProgress?: (done: number, total: number) => void,
): Promise<BulkPdfResult> {
  let done = 0;
  const fetched = await mapWithConcurrency(targets, FETCH_CONCURRENCY, async (target) => {
    try {
      const blob = await api.getBlob(`/work-orders/${target.id}/pdf`);
      return { target, bytes: await blob.arrayBuffer() };
    } catch {
      return { target, bytes: null };
    } finally {
      done += 1;
      onProgress?.(done, targets.length);
    }
  });

  const { PDFDocument } = await import('pdf-lib');
  const merged = await PDFDocument.create();
  const failed: BulkPdfTarget[] = [];
  for (const { target, bytes } of fetched) {
    if (!bytes) {
      failed.push(target);
      continue;
    }
    try {
      const source = await PDFDocument.load(bytes);
      const pages = await merged.copyPages(source, source.getPageIndices());
      for (const page of pages) merged.addPage(page);
    } catch {
      failed.push(target);
    }
  }

  if (merged.getPageCount() === 0) return { pdf: null, failed };
  const saved = await merged.save();
  return { pdf: new Blob([saved as BlobPart], { type: 'application/pdf' }), failed };
}

/** Hand a blob to the browser as a file download. */
export function downloadBlob(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}
